import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {Observable} from 'rxjs';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {buildDrawingSection} from '@common/utils/dist/utils/matching-scene-utils.js';

import {Action} from '@actions/action';
import {
  CreateSectionAction,
  SectionTarget,
} from '@actions/create-section.action';
import {UserStore} from '@stores/user.store';

export interface AddMatchingSectionInput {
  title: string;
  scene: APIM.AgentMatchingScene;
}

/** A short random prefix for this section's object ids (never "p__" or "_"). */
function idPrefix(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let prefix = 'g';
  for (let i = 0; i < 5; i++) {
    prefix += chars[Math.floor(Math.random() * chars.length)];
  }
  return prefix;
}

/**
 * Adds a drag and drop or line matching card proposed by the lesson chat to the end of the
 * current lesson, with its objects on the canvas and the answer key (match objects) set up,
 * like the section directives create them (individual scope).
 */
@Injectable({providedIn: 'root'})
export class AddMatchingSectionToLessonAction implements Action<
  AddMatchingSectionInput,
  PL2.AnySectionEntry
> {
  private _createSectionAction = inject(CreateSectionAction);
  private _translate = inject(TranslateService);
  private _userStore = inject(UserStore);

  execute(
    input: AddMatchingSectionInput,
    target?: SectionTarget,
  ): Observable<PL2.AnySectionEntry> {
    const lineMatching = input.scene.kind === 'line_matching';
    const parts = buildDrawingSection(input.scene, {
      userId: this._userStore.state().userId,
      idPrefix: idPrefix(),
    });
    return this._createSectionAction.execute({
      partialEntry: {
        n:
          input.title ||
          this._translate.instant(
            lineMatching
              ? 'shared-line-matching-section.nDefault'
              : 'shared-drag-and-drop-section.nDefault',
          ),
        sca: 1,
        ...parts,
      } as Partial<PL2.SharedDragAndDropSectionEntry>,
      entryType: lineMatching
        ? PL2.EntryType.SharedLineMatchingSection
        : PL2.EntryType.SharedDragAndDropSection,
      index: Number.MAX_SAFE_INTEGER,
      scope: PL2.SectionScope.Individual,
      target,
    });
  }
}
